/**
 * Centurion Matting - single source of truth for marketing copy, FAQs,
 * specs, and shared content blocks. Imported by pages and JSON-LD builders.
 *
 * Phase 1 corrections incorporated authentic site copy and added
 * INFRASTRUCTURE_HUB / INFRASTRUCTURE_FAQS.
 */

// 4 brand pillars (replaces generic stats from the earlier draft).
export const TRUST_PILLARS = [
    {
        head: 'High Quality',
        body: 'Manufactured from premium coastal Fir, engineered for strength, durability, and long service life.',
    },
    {
        head: 'Manufacturer Direct',
        body: 'Built at our facilities in Western Canada and sold directly to customers - eliminating broker markups.',
    },
    {
        head: 'Production Capacity',
        body: 'Large-scale manufacturing capacity to support projects ranging from dozens to thousands of mats.',
    },
    {
        head: 'Experience',
        body: 'Third-generation family-owned manufacturing expertise since 1955.',
    },
];

export const WHY_CENTURION = [
    {
        title: 'Coastal Hem-Fir Hi-Line #2',
        body:
            'Sourced from coastal British Columbia. Tight, consistent grain - the structural advantage behind every Centurion mat.',
    },
    {
        title: '12\u2033 Interlocking Pockets',
        body:
            'Engineered pocket geometry locks adjacent mats together so loads transfer across the assembly, not into the soil profile.',
    },
    {
        title: '177-Bolt Reinforced Pattern',
        body:
            'Field-proven bolting pattern resists racking under repeated tracked-equipment passes and heavy lift cycles.',
    },
];

export const ACCESS_MAT_SPEC = [
    { label: 'Standard size', value: '8 ft × 14 ft × 5 in' },
    { label: 'Wood', value: 'Coastal Hem-Fir Hi-Line #2' },
    { label: 'Build', value: '3 ply interlocking mats.' },
    { label: 'Pockets', value: '12″ pockets - to match your existing inventory.' },
    { label: 'Bolt pattern', value: '177-bolt reinforced' },
    { label: 'Surface area', value: '112 sq ft per mat' },
    { label: 'Truckload (typical)', value: '~55 mats per highway truck' },
];

export const RIG_MAT_SPEC = [
    { label: 'Typical sizes', value: '8 ft × 14 ft, 8 ft × 30 ft, 8 ft × 40 ft' },
    { label: 'Build', value: 'Heavy-duty timber, multi-layer with through-rods' },
    { label: 'Wood', value: 'Coastal Hem-Fir Hi-Line #2' },
    { label: 'Use', value: 'Drilling pads, workover rigs, frac spreads, production support' },
    { label: 'Custom', value: 'Built-to-spec sizing available - contact the mill' },
    { label: 'Related products', value: 'Pipeline skids, transition / ramp mats - configurable to spec' },
];

// Authentic spec table per Phase 1 correction brief.
export const CRANE_MAT_SPEC = [
    { label: 'Material', value: '#2 & BTR graded coastal Fir (minimal wane)' },
    { label: 'Bolts', value: 'Recessed, spot-welded nuts to resist vibration' },
    { label: 'Widths', value: '2′ - 10′' },
    { label: 'Lengths', value: '4′ - 40′' },
    { label: 'Thicknesses', value: '6″ - 18″' },
    { label: 'Custom sizes', value: 'Built to order' },
];

export const ACCESS_MATS_HUB = {
    eyebrow: 'Product Hub / Access Mats',
    title: 'Mats for your ground protection needs.',
    subtitle: 'Manufacturer Direct: Engineered for your ground protection needs.',
    intro: [
        'Centurion access mats are engineered for your ground protection needs. Three-ply interlocking timber assemblies with reinforced bolt patterns provide safe, stable access in environmentally sensitive areas, pipeline construction, and challenging terrains - stabilizing ground, preventing soil compaction and erosion, and protecting underground utilities.',
        'The workhorse of Western Canada\u2019s heavy industry. Three-ply interlocking timber assemblies milled in BC from coastal Hem-Fir Hi-Line #2, fitted with 12\u2033 pockets and locked together with our 177-bolt pattern. The result is an interlocking surface that distributes loads across many mats, not just the one under the track.',
        'Manufacturer Direct means you talk to the people who mill the mats. No reseller markup, no telephone game between the spec sheet and the yard.',
    ],
    applications: [
        'Access roads and well-site access',
        'Pipeline crossings and right-of-way (ROW)',
        'Staging areas and lay-down yards',
        'Tank farms and process facility builds',
        'Transmission line construction',
        'Wind farm and solar farm construction',
        'Helicopter pads',
        'Emergency egress and remediation',
    ],
};

export const RIG_MATS_HUB = {
    eyebrow: 'Product Hub / Rig Mats',
    title: 'Rig mats for drill pads and oilfield support.',
    intro: [
        'Heavier, longer and stiffer than access mats, Centurion rig mats are built for the loads that drill rigs, workover rigs and frac spreads put on the ground. Multi-layer timber with through-rods, sized in 8 ft widths and lengths up to 40 ft.',
        'Centurion also manufactures rig mats, pipeline skids, and transition / ramp mats - configurable to project specifications. Where access mats spread vehicle loads, rig mats provide a stable working deck for stationary heavy equipment over a long campaign.',
        'Need a non-standard size? Our Chemainus, BC mill can build to spec. Bring your engineering and we\u2019ll build to it.',
    ],
    applications: [
        'Drilling pads and surface holes',
        'Workover rigs',
        'Frac spreads and frac pads',
        'Production facility support',
        'Wellhead pads',
        'Service rig moves',
        'Pipeline skids',
        'Transition / ramp mats',
    ],
};

export const CRANE_MATS_HUB = {
    eyebrow: 'Product Hub / Crane Mats',
    title: 'Crane mats engineered for the highest pick loads.',
    intro: [
        'Centurion crane mats provide a secure foundation for heavy lifting equipment - protecting underground infrastructure and enhancing site safety on demanding lifts.',
        'Premium coastal-fir laminated timber, custom-built to spec. Widths from 2\u2032 to 10\u2032, lengths from 4\u2032 to 40\u2032, thicknesses from 6\u2033 to 18\u2033 - final spec set by your rigging plan and geotech.',
        'Built from #2 & BTR graded coastal Fir with recessed, spot-welded bolt nuts engineered to resist vibration loosening over the lift duration.',
    ],
    applications: [
        'Crawler crane outrigger pads',
        'Heavy-lift assemblies and picking grids',
        'Tower crane base support',
        'Module yard turn-arounds',
        'Heavy haul transport pad sets',
    ],
};

export const INFRASTRUCTURE_HUB = {
    eyebrow: 'Infrastructure',
    title: 'Ground Protection for Critical Infrastructure.',
    intro:
        'Access and crane matting protects existing utilities and pipelines from heavy machinery, stabilizes terrain for operational efficiency, preserves environments by reducing compaction and erosion, and boosts site safety - essential for operational excellence on construction projects.',
    applications: [
        {
            head: 'Pipeline Construction',
            body: 'Engineered access along pipeline corridors - protecting topsoil, reducing reclamation cost, and keeping crews moving through soft and frozen ground.',
        },
        {
            head: 'Power & Transmission Lines',
            body: 'Stable access for line construction, tower assemblies, and remote pull sites - built for the dispersed footprint of T&D work.',
        },
        {
            head: 'Wind Farm Construction',
            body: 'Crane pads and access roads spec\u2019d for nacelle lifts, tower segment moves, and component delivery.',
        },
        {
            head: 'Oil & Gas Drilling',
            body: 'Access roads, well-site pads, and rig mat decks for drilling, workover, and frac campaigns across BC and Alberta.',
        },
        {
            head: 'Environmentally Sensitive Areas',
            body: 'Reduces compaction, erosion, and vegetation damage - supporting reclamation and regulatory compliance.',
        },
        {
            head: 'Utility Protection',
            body: 'Prevents damage to underground utilities, buried pipelines, and shallow structures during heavy-equipment operations.',
        },
    ],
};

export const INFRASTRUCTURE_FAQS = [
    {
        q: 'What kind of infrastructure projects use ground protection matting?',
        a: 'Pipeline construction, power & transmission line builds, wind and solar farm construction, oil & gas drilling, and utility / underground work - anywhere heavy equipment must cross sensitive ground without damaging what\u2019s under it.',
    },
    {
        q: 'How does matting protect underground utilities?',
        a: 'Mats spread concentrated equipment loads across a wide footprint so the bearing pressure on existing utilities, pipelines, and shallow structures stays below safe limits - even under tracked and crawler loads.',
    },
    {
        q: 'Can Centurion supply matting for a multi-site infrastructure program?',
        a: 'Yes. Production from our Chemainus, BC mill scales for major projects and multi-year campaigns, with Manufacturer Direct lead times and dispatch from BC.',
    },
];

export const ACCESS_FAQS = [
    {
        q: 'How many access mats per kilometre?',
        a: 'A Centurion access road runs roughly 477 mats per kilometre on a straight corridor, plus an additional 25 mats per corner or turn. Use the Field Calculator above for an exact estimate against your route.',
    },
    {
        q: 'What is the best ground protection for soft soil in BC?',
        a: 'For soft, organic, or frost-susceptible soils typical of BC interior and coastal projects, three-ply interlocking timber access mats out-perform single-ply lumber and most synthetic composite mats. The 12\u2033 interlocking pockets transfer load between mats so the assembly behaves as one continuous deck.',
    },
    {
        q: 'Can access mats handle muskeg?',
        a: 'Yes - that\u2019s their design case. Muskeg can\u2019t carry concentrated loads, so the mat assembly spreads tracked-equipment and truck loads across many mats. For chronic muskeg corridors, doubled-up mat sections or transitional ramp mats at the entry/exit are common.',
    },
    {
        q: 'What wood are Centurion access mats made from?',
        a: 'Coastal Hem-Fir Hi-Line #2, milled in our Chemainus, BC facility. Tight, consistent grain and dimensional stability are why we\u2019ve specified Hem-Fir for three generations.',
    },
    {
        q: 'How are Centurion mats different from competitors?',
        a: '12\u2033 interlocking pockets, a 177-bolt reinforced design, and manufacturer-direct pricing. Order directly from the family-owned mill that builds the mats - not a broker or reseller.',
    },
];

export const RIG_FAQS = [
    {
        q: 'What\u2019s the difference between rig mats and access mats?',
        a: 'Access mats spread the rolling/tracked load of vehicles moving across them; rig mats provide a stiffer, heavier working deck for stationary equipment to sit on for weeks at a time. Rig mats are typically thicker, longer (up to 40 ft), and built to take repetitive concentrated loads from rig substructures.',
    },
    {
        q: 'Are Centurion rig mats CSA-rated?',
        a: 'Centurion rig mats are built to industry standard practice. Engineered load values for a specific lift or rig substructure are job-specific and signed off by your project engineer - Centurion supports that process with mill specs and assembly drawings.',
    },
    {
        q: 'What sizes are rig mats available in?',
        a: 'Common sizes are 8 ft × 14 ft, 8 ft × 30 ft, and 8 ft × 40 ft. Custom sizes can be milled - contact our sales team to discuss spec.',
    },
];

export const CRANE_FAQS = [
    {
        q: 'How thick are crane mats?',
        a: 'Standard Centurion crane mats run 6\u2033 to 18\u2033 thick. Final thickness is selected based on the rigging plan, crane footprint, and underlying ground conditions - your rigging or geotech engineer will specify.',
    },
    {
        q: 'What load can a crane mat support?',
        a: 'Industry-typical Centurion crane mats are commonly sized for ground-bearing pressures in the range used for heavy crawler picks. Specific allowable bearing pressure is engineered for your job - these are not blanket published values.',
    },
    {
        q: 'Can Centurion build custom crane mats?',
        a: 'Yes. Our Chemainus, BC mill regularly builds to non-standard sizes and bolt patterns when the lift plan calls for it. Send the rigging spec to sales and we\u2019ll quote.',
    },
    {
        q: 'What configurations do Centurion crane mats come in?',
        a: 'Two purpose-built configurations make up the bulk of what ships from the Chemainus mill: the 4 \u00d7 20 Pipeline Cable Mat with 5/8\u2033 \u00d7 6 \u00d7 26 IWRC Powerstrand wire rope and HD Flemish-eye terminations, and the 4 \u00d7 20 Notched Crane Mat with oversized 1 1/4\u2033 steel bolts and 5\u2032 \u00d7 17\u2033 precision-cut notch pick points. Both are custom-sized to your rigging spec.',
    },
];

export const PARTNERSHIP_FAQS = [
    {
        q: 'How does Centurion work with First Nations communities?',
        a: 'Through our partner Bear Valley Solutions - an Indigenous First Nations economic development organization - Centurion supports employment, procurement and joint-venture frameworks across Western Canada. Engagement is project-by-project and led by community priorities and existing partnership agreements.',
    },
    {
        q: 'What is Bear Valley Solutions?',
        a: 'Bear Valley Solutions is an Indigenous First Nations economic development organization based in Western Canada. They match Tier 1 service providers and manufacturers (including Centurion Matting) with First Nations partners through their Partner Program.',
    },
];

export const PARTNERSHIP_HUB = {
    eyebrow: 'Company / Indigenous Partnerships',
    title: 'Building lasting Indigenous partnerships across Western Canada.',
    intro:
        'Centurion Matting partners with Bear Valley Solutions, an Indigenous First Nations economic development organization that matches Tier 1 service providers with First Nations partners across Western Canada. We support employment, procurement, and joint-venture opportunities that respect Indigenous sovereignty and community priorities.',
    sections: [
        {
            title: 'Approach',
            body:
                'Our partnership work starts at the project level, led by the priorities of the community and the existing relationship between project owner and Nation. Centurion\u2019s role is to make manufacturer-direct supply available inside that framework - not to replace it.',
        },
        {
            title: 'How We Partner',
            bullets: [
                {
                    head: 'Employment',
                    body:
                        'Where projects in or adjacent to community territory require ground protection, we work with partners to prioritise Indigenous employment in mat handling, transport and field service.',
                },
                {
                    head: 'Procurement',
                    body:
                        'Manufacturer-direct supply means the procurement chain is short and the margins are visible. That makes it easier to build community-owned procurement programs around real, accountable numbers.',
                },
                {
                    head: 'Joint Ventures',
                    body:
                        'Through our partnership with Bear Valley Solutions, we participate in formal joint-venture agreements that give Nations a structural stake in the supply, not just a one-off sale.',
                },
            ],
        },
        {
            title: 'Communities Across Western Canada',
            body:
                'Centurion supplies ground protection on projects across British Columbia, Alberta, Saskatchewan, Manitoba, Yukon, Northwest Territories, and beyond. Where partnership frameworks exist, we work inside them; where they don\u2019t yet, we\u2019re ready to participate in building them.',
        },
    ],
};

export const ABOUT_HUB = {
    eyebrow: 'Company / About',
    title: 'Three generations. Multiple facilities. One standard.',
    // Verbatim authentic lead paragraph (per Phase 1 correction brief).
    lead:
        'Centurion Lumber Ltd. was established in 1955 to supply industrial and residential construction materials. Today, Centurion Matting Products supplies owners, contractors, and First Nations communities with premium, custom-built matting solutions delivered on schedule and on budget.',
    intro: [
        'Our team combines decades of experience in lumber manufacturing, major project delivery, logistics, and customer service. We manufacture in Western Canada using premium coastal Fir, providing durable, high-performance matting solutions to customers across the region.',
        'We also work alongside Indigenous partners to support economic development and participation in major infrastructure and resource projects throughout Western Canada.',
        "The standard hasn't changed: premium coastal Fir, interlocking finger construction, reinforced bolt patterns, and direct accountability from the mill floor to the field.",
    ],
};

// Illustrative timeline. 1955 founding is documented; later milestones are
// generalised framing of a multi-generation family operation.
export const ABOUT_TIMELINE = [
    { year: '1955', text: 'Founded as Centurion Lumber Ltd. in coastal BC, supplying industrial and residential construction materials.' },
    { year: 'Gen 2', text: 'Family transition; specialisation in timber matting for heavy industry begins.' },
    { year: 'Modern', text: 'Manufacturing at Chemainus, BC alongside a Calgary, AB sales office - running side-by-side as Centurion Matting Products.' },
    { year: 'Today', text: 'Third-generation family ownership. Manufacturer Direct supply across Western Canada.' },
    { year: 'Forward', text: 'Partnership with Bear Valley Solutions extends Indigenous First Nations participation across the matting supply chain.' },
];

export const PRODUCT_HUBS = [
    {
        to: '/access-mats',
        title: 'Access Mats',
        valueProp: '3-PLY INTERLOCKING Mats for your ground protection needs.',
        mediaKey: 'aerialCorridor',
    },
    {
        to: '/rig-mats',
        title: 'Rig Mats',
        valueProp: 'Heavy-duty timber decks for drilling, workover, frac, and pipeline skids.',
        mediaKey: 'snowField',
    },
    {
        to: '/crane-mats',
        title: 'Crane Mats',
        valueProp: 'Laminated coastal Fir for crawler crane outriggers and heavy-lift picks.',
        mediaKey: 'cranePad',
    },
    {
        to: '/projects/infrastructure',
        title: 'Infrastructure Projects',
        valueProp: 'Ground protection across pipeline, power, wind, oil & gas and utility work.',
        mediaKey: 'muskeg',
    },
];

// Calculator constants - single source of truth for both UI and tests.
export const CALC = {
    accessPerKm: 477,
    accessPerCorner: 25,
    matLengthFt: 14,
    matWidthFt: 8,
    matAreaSqFt: 8 * 14, // 112
    matsPerTruck: 55,
    wasteFactor: 0.05, // retained for backward compat; UI toggle removed with lease mode retirement
    limits: {
        kmMax: 500,
        cornersMax: 200,
        dimensionMax: 10000,
    },
};
